import { Position } from "@xyflow/react";
import { memo } from "react";
import { MdDelete } from "react-icons/md";
import CircleButton from "widgets/controls/buttons/circle-button";
import { ZoneNodeData } from "../flow-utils";
import RectangleHandle from "../../handles/rectangle";
import { TbAlertTriangle, TbCircleLetterZ } from "react-icons/tb";
import CircleMenu from "../../components/circle-menu";
import { useTranslation } from "react-i18next";
import { ModelFilterDropdown } from "models/dropdowns/modelFilter";
import TooltipHorizon from "widgets/controls/tooltip";

function ZoneNode(props: { data: ZoneNodeData }) {
  const { t } = useTranslation();
  const { data } = props;
  const leafNodes = data.leafNodes || [];
  const modelType = ModelFilterDropdown.find((m) =>
    m.value.includes(data.leafNodes[0]?.device?.model)
  )?.label;
  return (
    <div
      className={`border-stone-400 min-w-52 rounded-3xl border-2 bg-white py-2 shadow-lg`}
      style={{ width: data.width, height: data.height }}
      key={data.id}
    >
      {/* Node content */}
      <div className="flex h-full flex-col justify-between p-1">
        <div className="relative mx-2 flex justify-center">
          <div
            className="truncate text-center text-lg font-bold text-primary"
            style={{ maxWidth: data.width - 10 }}
          >
            {data.name}
          </div>
          <div className="absolute right-0">
            <TbCircleLetterZ className="text-3xl text-red-500" />
          </div>
        </div>
        {data.data && (
          <div className="px-2">
            <div className="truncate text-xs font-bold text-primary">
              {t("generic.usage")}: {data.data.usage.toFixed(2)}{" "}
              {t(`generic.unit`, { context: modelType.toLowerCase() })}
            </div>

            {data.data.info.find((info) => info.name === "correlation") && (
              <div className="truncate text-xs font-bold text-primary">
                CORRELATION:{" "}
                {
                  data.data.info.find((info) => info.name === "correlation")
                    ?.value
                }
              </div>
            )}
          </div>
        )}

        {/* Node content */}
        <div className="nowheel min-h-0 flex-1 overflow-y-auto " key={data.id}>
          <div className="flex flex-col gap-2 px-2 py-3">
            {leafNodes.map((leaf, index) => {
              const isOnline = leaf.data?.info.find(
                (info) => info.name === "online"
              )?.value
                ? true
                : false;

              const isHealthy = leaf.data?.info?.find(
                (info) => info.name === "dataCoverage"
              )?.value
                ? true
                : false;

              const dataCoverageInfo = leaf.data?.info?.find(
                (info) => info.name === "dataCoverage"
              );
              const hasRawNearStart = dataCoverageInfo?.conditions
                ?.hasRawNearStart
                ? t("generic.true")
                : t("generic.false");
              const hasRawNearEnd = dataCoverageInfo?.conditions?.hasRawNearEnd
                ? t("generic.true")
                : t("generic.false");
              return (
                <div
                  key={leaf.device.snr}
                  className="flex items-center justify-between"
                >
                  <div className="flex flex-col">
                    <div className="flex items-center gap-2">
                      <div
                        className="truncate text-sm font-bold text-primary"
                        style={{ maxWidth: data.width - 10 }}
                      >
                        {leaf.label}
                      </div>
                      <span
                        className={`h-1.5 w-1.5 rounded-full ${
                          isOnline ? "bg-green-500" : "bg-red-500"
                        }`}
                      />
                    </div>
                    <div className="truncate text-xs text-secondary">
                      SNR: {leaf.device.snr}
                    </div>

                    {leaf.data && (
                      <>
                        <div className="truncate text-xs font-bold text-primary">
                          {t("generic.usage")}: {leaf.data.usage}{" "}
                          {t(`generic.unit`, {
                            context: modelType.toLowerCase(),
                          })}
                        </div>
                      </>
                    )}
                  </div>

                  <div className="flex">
                    {!isHealthy && (
                      <TooltipHorizon
                        extra="whitespace-normal"
                        placement="top"
                        trigger={
                          <TbAlertTriangle className="text-3xl text-yellow-500" />
                        }
                        content={
                          <span className="block whitespace-normal break-words text-sm leading-snug">
                            <div>
                              {t("generic.has.raw.near.start")}:{" "}
                              {hasRawNearStart}
                            </div>
                            <div>
                              {t("generic.has.raw.near.end")}: {hasRawNearEnd}
                            </div>
                          </span>
                        }
                      />
                    )}
                    <CircleMenu data={leaf} />
                  </div>
                  {data.onRemoveDevice && (
                    <CircleButton
                      extra="nodrag"
                      icon={MdDelete}
                      variant="ghost"
                      size="xs"
                      onClick={() =>
                        data.onRemoveDevice?.(data.id, leaf.device.snr)
                      }
                    />
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Bottom buttons - only in builder mode */}
        {data.onDelete && (
          <div className="nodrag flex justify-end">
            <CircleButton
              label="REMOVE"
              size="xs"
              color="red"
              onClick={() => data.onDelete?.(data.id)}
            />
          </div>
        )}
      </div>

      {/* Target handle - flow in */}
      <RectangleHandle
        type="target"
        position={data.targetPosition ?? Position.Left}
        width={data.width}
        height={data.height}
      />

      {/* Source handle - flow out */}
      <RectangleHandle
        type="source"
        position={data.sourcePosition ?? Position.Right}
        width={data.width}
        height={data.height}
      />
    </div>
  );
}

export default memo(ZoneNode);
